import React, { useState, useMemo, useRef, useLayoutEffect, useEffect, useCallback } from 'react';
import styled from 'styled-components';
import { Button, gtagEvent, vars } from '@agendor/ui';
import { Rich } from '@agendor/shared';
import BusinessService, {
    BusinessDetails,
    BusinessServiceItem,
    BusinessStats,
    hasPaidAccess,
} from '../business-service';
import MonthNavigation from '../components/MonthNavigation';
import { useSectionVisibility } from './section-visibility-context';

type Worker = {
    id: number;
    name: string;
};

type Service = BusinessServiceItem;

const Container = styled.div`
    display: flex;
    flex-direction: column;
    gap: 32px;
    max-width: 800px;
    margin: 0 auto;
    width: 100%;
`;

const Header = styled.div`
    display: flex;
    flex-direction: column;
    gap: 24px;
`;

const HeaderRow = styled.div`
    display: flex;
    justify-content: space-between;
    align-items: center;
    flex-wrap: wrap;
    gap: 16px;
`;

const Title = styled.h1`
    font-size: 1.5rem;
    color: ${vars.foregroundColor};
    margin: 0;
    font-weight: 600;
`;

const PeriodButtons = styled.div`
    display: flex;
    gap: 8px;
    flex-wrap: wrap;
`;

const PeriodButton = styled.button<{ active: boolean }>`
    padding: 8px 12px;
    border-radius: 6px;
    border: 1px solid ${(p) => (p.active ? vars.foregroundColor : vars.dividerColor)};
    background-color: ${(p) => (p.active ? vars.primaryColorLight : 'transparent')};
    color: ${vars.foregroundColor};
    font: inherit;
    font-size: 1rem;
    font-weight: 400;
    cursor: pointer;

    &:hover {
        border-color: ${vars.foregroundColor};
    }
`;

const DateRangeDisplay = styled.div`
    display: inline-flex;
    align-items: center;
    height: 36px;
    padding: 0 16px;
    background-color: ${vars.backgroundColorBright};
    border-radius: 6px;
    border: 1px solid ${vars.dividerColor};
    font-size: 1rem;
    color: ${vars.foregroundColor};
    text-transform: capitalize;
`;

const StatsGrid = styled.div`
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
    gap: 16px;
`;

const StatCard = styled.div.attrs({ 'data-stat-card': 'true' } as React.HTMLAttributes<HTMLDivElement>)<{
    variant?: 'default' | 'success' | 'warning' | 'danger';
}>`
    background-color: #fff;
    border: 1px solid ${vars.dividerColor};
    border-radius: 8px;
    padding: 20px;
    display: flex;
    flex-direction: column;
    gap: 8px;
    min-width: 0;

    ${(p) =>
        p.variant === 'success' &&
        `
        background-color: ${vars.successTintSoft};
    `}

    ${(p) =>
        p.variant === 'warning' &&
        `
        background-color: ${vars.warningTintSoft};
    `}

    ${(p) =>
        p.variant === 'danger' &&
        `
        background-color: ${vars.dangerTintSoft};
    `}
`;

const StatLabelRow = styled.div`
    display: flex;
    align-items: center;
    gap: 6px;
`;

const StatLabel = styled.span`
    font-size: 0.85rem;
    color: #666;
    text-transform: uppercase;
    letter-spacing: 0.5px;
    font-weight: 500;
`;

const InfoIcon = styled.button`
    width: 14px;
    height: 14px;
    border-radius: 50%;
    border: 1px solid #999;
    background: transparent;
    color: #999;
    font-size: 9px;
    font-weight: 600;
    cursor: pointer;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    padding: 0;
    position: relative;
    flex-shrink: 0;
    transform: translateY(-1px);

    &:hover,
    &:focus {
        border-color: ${vars.foregroundColor};
        color: ${vars.foregroundColor};
    }
`;

const Tooltip = styled.span`
    position: absolute;
    bottom: calc(100% + 8px);
    left: 50%;
    transform: translateX(-50%);
    background: ${vars.foregroundColor};
    color: #fff;
    padding: 6px 10px;
    border-radius: 4px;
    font-size: 0.85rem;
    white-space: nowrap;
    font-weight: 400;
    text-transform: none;
    letter-spacing: 0;
    z-index: 10;

    &::after {
        content: '';
        position: absolute;
        top: 100%;
        left: 50%;
        transform: translateX(-50%);
        border: 5px solid transparent;
        border-top-color: ${vars.foregroundColor};
    }
`;

const StatValue = styled.span`
    font-size: 2rem;
    color: ${vars.foregroundColor};
    font-weight: 700;
    line-height: 1;
`;

const StatSubtext = styled.span`
    font-size: 0.85rem;
    color: #888;
`;

const Section = styled.section`
    display: flex;
    flex-direction: column;
    gap: 16px;
`;

const SectionTitle = styled.h2`
    font-size: 1.1rem;
    color: ${vars.foregroundColor};
    margin: 0;
    font-weight: 600;
    display: flex;
    align-items: center;
    gap: 8px;
`;

const TableWrapper = styled.div`
    overflow-x: auto;
    border: 1px solid ${vars.dividerColor};
    border-radius: 8px;
    background-color: #fff;
`;

const Table = styled.table`
    width: 100%;
    border-collapse: collapse;
`;

const Th = styled.th`
    text-align: left;
    padding: 12px 16px;
    font-size: 0.8rem;
    color: #666;
    text-transform: uppercase;
    letter-spacing: 0.5px;
    font-weight: 600;
    background-color: ${vars.backgroundColorBright};
    border-bottom: 1px solid ${vars.dividerColor};
    white-space: nowrap;

    &:last-child {
        text-align: right;
    }
`;

const Td = styled.td`
    padding: 12px 16px;
    font-size: 0.95rem;
    color: ${vars.foregroundColor};
    border-bottom: 1px solid ${vars.dividerColor};

    &:last-child {
        text-align: right;
        font-weight: 600;
    }

    tr:last-child & {
        border-bottom: none;
    }
`;

const ServiceRank = styled.div`
    display: flex;
    align-items: center;
    gap: 12px;
`;

const RankNumber = styled.span<{ rank: number }>`
    width: 24px;
    height: 24px;
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 0.75rem;
    font-weight: 700;
    background-color: ${(p) =>
        p.rank === 1 ? '#FFD700' : p.rank === 2 ? '#C0C0C0' : p.rank === 3 ? '#CD7F32' : vars.backgroundColorDark};
    color: ${(p) => (p.rank <= 3 ? '#333' : '#666')};
`;

const UpgradeHint = styled.div`
    background: ${vars.backgroundColorDark};
    border: 1px solid ${vars.dividerColor};
    border-radius: 8px;
    padding: 16px;

    p {
        margin: 0 0 12px 0;
        font-size: 0.9rem;
        color: #666;
    }
`;

type Period = '7d' | '30d' | 'month';

function formatPercent(value: number, total: number): string {
    if (total === 0) return '0%';
    return `${Math.round((value / total) * 100)}%`;
}

function formatCurrency(valueInCents: number): string {
    return (valueInCents / 100).toLocaleString('pt-BR', {
        style: 'currency',
        currency: 'BRL',
    });
}

function formatCurrencyCompact(valueInCents: number): { display: string; full: string; isCompact: boolean } {
    const value = valueInCents / 100;
    const full = value.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });

    if (value < 1000) {
        return { display: full, full, isCompact: false };
    } else if (value < 1000000) {
        const compact = (value / 1000).toLocaleString('pt-BR', { maximumFractionDigits: 1 });
        return { display: `R$ ${compact}k`, full, isCompact: true };
    } else {
        const compact = (value / 1000000).toLocaleString('pt-BR', { maximumFractionDigits: 1 });
        return { display: `R$ ${compact}M`, full, isCompact: true };
    }
}

function InfoWithTooltip({ text }: { text: string }) {
    const [showTooltip, setShowTooltip] = useState(false);

    return (
        <InfoIcon
            type="button"
            onClick={() => setShowTooltip(!showTooltip)}
            onMouseEnter={() => setShowTooltip(true)}
            onMouseLeave={() => setShowTooltip(false)}
            onBlur={() => setShowTooltip(false)}
        >
            i{showTooltip && <Tooltip>{text}</Tooltip>}
        </InfoIcon>
    );
}

function RevenueStatCard({ valueInCents }: { valueInCents: number }) {
    const containerRef = useRef<HTMLDivElement>(null);
    const [needsCompact, setNeedsCompact] = useState(false);

    const full = formatCurrency(valueInCents);
    const compact = formatCurrencyCompact(valueInCents);

    useLayoutEffect(() => {
        const checkSize = () => {
            if (!containerRef.current) return;

            const statCard = containerRef.current.closest('[data-stat-card]');
            if (!statCard) return;

            const availableWidth = statCard.getBoundingClientRect().width - 40;

            const measureSpan = document.createElement('span');
            measureSpan.style.cssText =
                'position:absolute;visibility:hidden;white-space:nowrap;font-size:2rem;font-weight:700;';
            measureSpan.textContent = full;
            document.body.appendChild(measureSpan);
            const textWidth = measureSpan.offsetWidth;
            document.body.removeChild(measureSpan);

            setNeedsCompact(textWidth > availableWidth);
        };

        checkSize();
        window.addEventListener('resize', checkSize);
        return () => window.removeEventListener('resize', checkSize);
    }, [valueInCents, full]);

    return (
        <StatCard>
            <StatLabelRow>
                <StatLabel data-testid="stats-revenue">Faturamento</StatLabel>
                {needsCompact && <InfoWithTooltip text={full} />}
            </StatLabelRow>
            <div ref={containerRef}>
                <StatValue>{needsCompact ? compact.display : full}</StatValue>
            </div>
            <StatSubtext>valor dos agendamentos</StatSubtext>
        </StatCard>
    );
}

const getMaxMonth = () => {
    const now = new Date();
    now.setMonth(now.getMonth() - 1);
    return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
};

const isValidMonth = (month: string) => month <= getMaxMonth();

const parseRouteSegments = (segments: string[]): { period: Period; month: string } => {
    const [first, second] = segments;
    if (first === '7d') return { period: '7d', month: getMaxMonth() };
    if (first === '30d') return { period: '30d', month: getMaxMonth() };
    if (first === 'month' && second && /^\d{4}-\d{2}$/.test(second)) {
        if (!isValidMonth(second)) return { period: '7d', month: getMaxMonth() };
        return { period: 'month', month: second };
    }
    if (first === 'month') {
        return { period: 'month', month: getMaxMonth() };
    }
    return { period: '7d', month: getMaxMonth() };
};

export default function StatsSection({
    service,
    business,
    workers,
    services,
    modalSegments,
    onNavigate,
    onNavigateToUpgrade,
}: {
    service: BusinessService;
    business: BusinessDetails | null;
    workers: Worker[];
    services: Service[];
    modalSegments: string[];
    onNavigate: (segments: string[], replace?: boolean) => void;
    onNavigateToUpgrade: () => void;
}) {
    const isVisible = useSectionVisibility();

    const routeState = useMemo(() => parseRouteSegments(modalSegments), [modalSegments]);
    const period = routeState.period;
    const selectedMonth = routeState.month;

    const [stats, setStats] = useState<BusinessStats | null>(null);
    const [isLoading, setIsLoading] = useState(true);
    const [loadError, setLoadError] = useState<string | null>(null);
    const cacheRef = useRef<Map<string, BusinessStats>>(new Map());

    const setPeriod = (p: Period) => {
        if (p === 'month') {
            onNavigate(['month', selectedMonth], true);
        } else {
            onNavigate([p], true);
        }
    };

    const setSelectedMonth = (month: string) => {
        onNavigate(['month', month], true);
    };

    useEffect(() => {
        if (!isVisible) return;
        if (modalSegments.length === 0) {
            onNavigate(['7d'], true);
            return;
        }
        const [first, second] = modalSegments;
        if (first === 'month' && second && !isValidMonth(second)) {
            onNavigate(['7d'], true);
        }
    }, [isVisible, modalSegments, onNavigate]);

    const lacksPaidAccess = business !== null && !hasPaidAccess(business);
    const hasConfirmation = business?.bookingConfirmation !== null;
    const hasCheckIn = business?.checkInEnabled ?? false;

    const cacheKey = period === 'month' ? `month:${selectedMonth}` : period;

    const loadStats = useCallback(
        async (forceRefresh = false) => {
            if (period === 'month' && lacksPaidAccess) {
                setIsLoading(false);
                return;
            }

            const cached = cacheRef.current.get(cacheKey);
            if (cached && !forceRefresh) {
                setStats(cached);
                setIsLoading(false);
                return;
            }

            setIsLoading(true);
            setLoadError(null);
            try {
                const params = period === 'month' ? { period: 'month' as const, month: selectedMonth } : { period };
                const data = await service.getStats(params);
                cacheRef.current.set(cacheKey, data);
                setStats(data);
            } catch (error) {
                console.error('Failed to load stats:', error);
                setLoadError('Erro ao carregar estatísticas.');
            } finally {
                setIsLoading(false);
            }
        },
        [service, period, selectedMonth, lacksPaidAccess, cacheKey],
    );

    useEffect(() => {
        cacheRef.current.clear();
        setStats(null);
        setLoadError(null);
        setIsLoading(true);
    }, [service]);

    useEffect(() => {
        if (!isVisible) return;
        loadStats();
    }, [isVisible, loadStats]);

    const serviceRanking = useMemo(() => {
        if (!stats) return [];
        return stats.servicesCounts
            .map((sc) => {
                const service = services.find((s) => s.id === sc.serviceId);
                if (!service) return null;
                return { ...sc, name: service.name };
            })
            .filter((s): s is NonNullable<typeof s> => s !== null)
            .sort((a, b) => b.count - a.count)
            .slice(0, 5);
    }, [stats, services]);

    const workerStatsWithNames = useMemo(() => {
        if (!stats) return [];
        return stats.workerStats
            .map((ws) => {
                const worker = workers.find((w) => w.id === ws.workerId);
                if (!worker) return null;
                return { ...ws, name: worker.name };
            })
            .filter((w): w is NonNullable<typeof w> => w !== null)
            .sort((a, b) => b.bookings - a.bookings);
    }, [stats, workers]);

    const handlePeriodClick = (p: Period) => {
        gtagEvent('stats_period_click', { action: p });
        setPeriod(p);
    };

    if (!isVisible) return null;

    const showMonthUpsell = lacksPaidAccess && period === 'month';
    const cancellationRate = stats ? formatPercent(stats.cancelledBookings, stats.totalBookings) : '0%';
    const confirmationRate = stats
        ? formatPercent(stats.confirmedBookings ?? 0, stats.totalBookings - stats.cancelledBookings)
        : '0%';
    const attendanceRate = stats
        ? formatPercent(stats.checkedInBookings ?? 0, stats.totalBookings - stats.cancelledBookings)
        : '0%';
    const noShows = stats ? stats.totalBookings - stats.cancelledBookings - (stats.checkedInBookings ?? 0) : 0;

    return (
        <Container>
            <Header>
                <HeaderRow>
                    <Title>Estatísticas</Title>
                    <PeriodButtons>
                        <PeriodButton active={period === '7d'} onClick={() => handlePeriodClick('7d')}>
                            7 dias
                        </PeriodButton>
                        <PeriodButton active={period === '30d'} onClick={() => handlePeriodClick('30d')}>
                            30 dias
                        </PeriodButton>
                        <PeriodButton active={period === 'month'} onClick={() => handlePeriodClick('month')}>
                            Mês
                        </PeriodButton>
                    </PeriodButtons>
                </HeaderRow>
                {period === 'month' &&
                    !lacksPaidAccess &&
                    (() => {
                        const [year, monthNum] = selectedMonth.split('-').map(Number);
                        const monthValue = { year, month: monthNum };
                        const maxMonth = new Rich.Month(new Date()).adding({ months: -1 }).plain;
                        const maxMonthString = `${maxMonth.year}-${String(maxMonth.month).padStart(2, '0')}`;
                        return (
                            <div>
                                <MonthNavigation
                                    month={monthValue}
                                    onChange={(next) => {
                                        gtagEvent('stats_month_nav_click', {
                                            action:
                                                next.year < year || (next.year === year && next.month < monthNum)
                                                    ? 'prev'
                                                    : 'next',
                                        });
                                        setSelectedMonth(`${next.year}-${String(next.month).padStart(2, '0')}`);
                                    }}
                                    maxMonth={maxMonth}
                                    overlay={
                                        <input
                                            type="month"
                                            value={selectedMonth}
                                            onChange={(e) => setSelectedMonth(e.target.value)}
                                            max={maxMonthString}
                                            style={{
                                                position: 'absolute',
                                                opacity: 0,
                                                inset: 0,
                                                cursor: 'pointer',
                                            }}
                                            onClick={(e) => {
                                                if ('showPicker' in HTMLInputElement.prototype) {
                                                    try {
                                                        (e.target as HTMLInputElement).showPicker();
                                                    } catch {
                                                        // showPicker not supported
                                                    }
                                                }
                                            }}
                                        />
                                    }
                                />
                            </div>
                        );
                    })()}
                {(period === '7d' || period === '30d') && stats && (
                    <div>
                        <DateRangeDisplay>
                            {(() => {
                                const start = new Date(
                                    stats.startDate.year,
                                    stats.startDate.month - 1,
                                    stats.startDate.day,
                                );
                                const end = new Date(stats.endDate.year, stats.endDate.month - 1, stats.endDate.day);
                                const formatDate = (d: Date) =>
                                    d.toLocaleDateString('pt-BR', { day: 'numeric', month: 'short' });
                                return `${formatDate(start)} – ${formatDate(end)}`;
                            })()}
                        </DateRangeDisplay>
                    </div>
                )}
            </Header>

            {showMonthUpsell ? (
                <UpgradeHint>
                    <p>
                        Com uma assinatura ativa, você pode visualizar estatísticas de qualquer mês.
                        {business?.subscription?.isTrialEligible && <strong> Teste grátis por 7 dias.</strong>}
                    </p>
                    <Button
                        onClick={() => {
                            gtagEvent('stats_upsell_click');
                            onNavigateToUpgrade?.();
                        }}
                    >
                        {business?.subscription?.isTrialEligible ? 'Testar grátis' : 'Reativar'}
                    </Button>
                </UpgradeHint>
            ) : isLoading ? (
                <div style={{ textAlign: 'center', padding: '40px', color: '#888' }}>Carregando estatísticas...</div>
            ) : loadError ? (
                <div style={{ textAlign: 'center', padding: '40px', color: '#888' }}>
                    <div style={{ marginBottom: '12px', color: vars.foregroundColor }}>{loadError}</div>
                    <Button onClick={() => loadStats()}>Tentar novamente</Button>
                </div>
            ) : !stats ? (
                <div style={{ textAlign: 'center', padding: '40px', color: '#888' }}>
                    Não foi possível carregar as estatísticas.
                </div>
            ) : (
                <>
                    {/* Agendamentos */}
                    <Section>
                        <SectionTitle>Agendamentos</SectionTitle>
                        <StatsGrid>
                            <StatCard>
                                <StatLabel>Total</StatLabel>
                                <StatValue>{stats.totalBookings}</StatValue>
                                <StatSubtext>agendamentos no período</StatSubtext>
                            </StatCard>
                            <StatCard variant={stats.cancelledBookings > 0 ? 'warning' : 'default'}>
                                <StatLabel>Cancelamentos</StatLabel>
                                <StatValue>{stats.cancelledBookings}</StatValue>
                                <StatSubtext>{cancellationRate} do total</StatSubtext>
                            </StatCard>
                            <RevenueStatCard valueInCents={stats.totalRevenueInCents} />
                        </StatsGrid>
                    </Section>

                    {/* Confirmações */}
                    {hasConfirmation && stats.confirmedBookings !== undefined && (
                        <Section>
                            <SectionTitle>Confirmações</SectionTitle>
                            <StatsGrid>
                                <StatCard variant="success">
                                    <StatLabel>Confirmados</StatLabel>
                                    <StatValue>{stats.confirmedBookings}</StatValue>
                                    <StatSubtext>{confirmationRate} de taxa</StatSubtext>
                                </StatCard>
                            </StatsGrid>
                        </Section>
                    )}

                    {/* Check-in */}
                    {hasCheckIn && stats.checkedInBookings !== undefined && (
                        <Section>
                            <SectionTitle>Check-in</SectionTitle>
                            <StatsGrid>
                                <StatCard variant="success">
                                    <StatLabel>Realizados</StatLabel>
                                    <StatValue>{stats.checkedInBookings}</StatValue>
                                    <StatSubtext>{attendanceRate} comparecimento</StatSubtext>
                                </StatCard>
                                <StatCard variant={noShows > 0 ? 'danger' : 'default'}>
                                    <StatLabel>No-shows</StatLabel>
                                    <StatValue>{noShows}</StatValue>
                                    <StatSubtext>não compareceram</StatSubtext>
                                </StatCard>
                            </StatsGrid>
                        </Section>
                    )}

                    {/* Clientes */}
                    <Section>
                        <SectionTitle>Clientes</SectionTitle>
                        <StatsGrid>
                            <StatCard>
                                <StatLabel>Total</StatLabel>
                                <StatValue>{stats.totalClients}</StatValue>
                                <StatSubtext>clientes cadastrados</StatSubtext>
                            </StatCard>
                            <StatCard variant={stats.newClients > 0 ? 'success' : 'default'}>
                                <StatLabel>Novos</StatLabel>
                                <StatValue>{stats.newClients}</StatValue>
                                <StatSubtext>primeiro agendamento no período</StatSubtext>
                            </StatCard>
                        </StatsGrid>
                    </Section>

                    {/* Serviços populares */}
                    <Section>
                        <SectionTitle>Serviços mais populares</SectionTitle>
                        {serviceRanking.length > 0 ? (
                            <TableWrapper>
                                <Table>
                                    <thead>
                                        <tr>
                                            <Th>Serviço</Th>
                                            <Th>Agendamentos</Th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {serviceRanking.map((service, index) => (
                                            <tr key={service.serviceId}>
                                                <Td>
                                                    <ServiceRank>
                                                        <RankNumber rank={index + 1}>{index + 1}</RankNumber>
                                                        {service.name}
                                                    </ServiceRank>
                                                </Td>
                                                <Td>{service.count}</Td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </Table>
                            </TableWrapper>
                        ) : (
                            <div style={{ textAlign: 'center', padding: '40px', color: '#888' }}>
                                Nenhum serviço agendado no período.
                            </div>
                        )}
                    </Section>

                    {/* Por profissional */}
                    {workerStatsWithNames.length > 0 && (
                        <Section>
                            <SectionTitle>Por profissional</SectionTitle>
                            <TableWrapper>
                                <Table>
                                    <thead>
                                        <tr>
                                            <Th>Profissional</Th>
                                            <Th>Agendamentos</Th>
                                            <Th>Cancelamentos</Th>
                                            {hasConfirmation && stats.confirmedBookings !== undefined && (
                                                <Th>Confirmados</Th>
                                            )}
                                            {hasCheckIn && stats.checkedInBookings !== undefined && <Th>Check-ins</Th>}
                                            <Th>Faturamento</Th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {workerStatsWithNames.map((worker) => (
                                            <tr key={worker.workerId}>
                                                <Td>{worker.name}</Td>
                                                <Td style={{ textAlign: 'center' }}>{worker.bookings}</Td>
                                                <Td style={{ textAlign: 'center' }}>{worker.cancelled}</Td>
                                                {hasConfirmation && stats.confirmedBookings !== undefined && (
                                                    <Td style={{ textAlign: 'center' }}>{worker.confirmed}</Td>
                                                )}
                                                {hasCheckIn && stats.checkedInBookings !== undefined && (
                                                    <Td style={{ textAlign: 'center' }}>{worker.checkedIn}</Td>
                                                )}
                                                <Td>{formatCurrency(worker.revenueInCents)}</Td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </Table>
                            </TableWrapper>
                        </Section>
                    )}
                </>
            )}
        </Container>
    );
}
